'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { ArrowRight, Shield, Zap, Code2, EyeOff, Target, Briefcase, ScanSearch, BarChart, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import { softwareApplicationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const roles = [
  {
    tag: 'For builders',
    color: 'text-blue-400',
    border: 'border-blue-500/20',
    bg: 'bg-blue-500/10',
    title: 'Ship without becoming a security expert',
    body: 'You moved fast with AI tools. Add one line of code (or a Worker, or a container) and get runtime protection - without learning OWASP or wiring up a WAF.',
    points: [
      { icon: Zap, t: 'One-step install', d: 'SDK for Node/Next.js, a Cloudflare Worker, or a Docker proxy. Pick what fits your stack.' },
      { icon: Code2, t: 'Clear blocks', d: 'See exactly what was blocked and why - in plain language, not security jargon.' },
    ],
  },
  {
    tag: 'For teams',
    color: 'text-emerald-400',
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/10',
    title: 'Visibility into what your AI app actually does',
    body: 'One dashboard for every install. See blocked threats, data-leak alerts and traffic across your apps - with detection running on your own infrastructure.',
    points: [
      { icon: ScanSearch, t: 'Free security scan', d: 'Point us at your live app and get a plain-language report of what is exposed.' },
      { icon: BarChart, t: 'Activity & audit logs', d: 'A record of requests, blocks and alerts you can review and export.' },
    ],
  },
  {
    tag: 'For founders',
    color: 'text-rose-400',
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/10',
    title: 'Don\u2019t let a default setting end your company',
    body: 'A single open database or leaked key can expose every customer you have. Silker is the layer that catches that class of mistake before someone else does.',
    points: [
      { icon: Shield, t: 'Brand & data safety', d: 'Stop sensitive data and secrets from leaking, and block prompt-injection abuse of your AI.' },
      { icon: Briefcase, t: 'Cheaper than an incident', d: 'A fraction of the cost of a dedicated security hire - or a breach.' },
    ],
  },
];

const benefitsBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Benefits', url: `${SITE_URL}/benefits` },
]);

export default function BenefitsPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(benefitsBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-24">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-20" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                What you get
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-white">
                Runtime protection for apps built with AI
              </h1>
              <p className="text-base text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                Apps shipped with Lovable, Bolt, Cursor, Replit or v0 are fast to build and easy to get wrong. Silker
                adds the security layer they skip - scan, protect at runtime, and monitor.
              </p>
            </div>

            {/* Honest capability strip (no fabricated metrics) */}
            <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-24" data-scroll-animate="fade-up">
              {[
                { t: 'Blocks live', d: 'Threats stopped at runtime, not flagged in a report after the fact.' },
                { t: 'Your infra', d: 'Detection runs where your app runs. Traffic never routes through us.' },
                { t: 'AI-aware', d: 'Catches prompt injection and data leaks a generic WAF never sees.' },
              ].map((s, i) => (
                <div key={i} className="bg-neutral-950 p-6">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mb-3" />
                  <div className="text-white font-medium mb-1">{s.t}</div>
                  <p className="text-sm text-neutral-400">{s.d}</p>
                </div>
              ))}
            </div>

            {/* By role */}
            <div className="space-y-6 mb-24">
              {roles.map((r, i) => (
                <div
                  key={i}
                  className="rounded-none border border-white/10 bg-neutral-900/30 p-8 md:p-10"
                  data-scroll-animate="fade-up"
                >
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-none ${r.bg} ${r.color} text-xs font-medium mb-4 border ${r.border}`}>
                    {r.tag}
                  </span>
                  <h2 className="text-2xl font-bold text-white mb-3">{r.title}</h2>
                  <p className="text-neutral-400 leading-relaxed mb-6 max-w-2xl">{r.body}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {r.points.map((p, j) => {
                      const Icon = p.icon;
                      return (
                        <div key={j} className="flex items-start gap-3">
                          <div className={`mt-0.5 p-1.5 rounded-none ${r.bg} border ${r.border}`}>
                            <Icon className={`w-4 h-4 ${r.color}`} />
                          </div>
                          <div>
                            <h4 className="text-white font-medium text-sm">{p.t}</h4>
                            <p className="text-neutral-500 text-sm">{p.d}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Industry context (clearly sourced, not our metric) */}
            <div className="rounded-none border border-white/10 bg-neutral-900/30 p-8 text-center mb-20" data-scroll-animate="fade-up">
              <Target className="w-8 h-8 text-rose-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-1">$4.44M</h3>
              <p className="text-sm text-neutral-400">Average cost of a data breach in 2026 - the kind of number a single open database can trigger.</p>
              <Link
                href="https://www.ibm.com/think/x-force/2026-cost-of-a-data-breach-navigating-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-rose-400 mt-3 transition-colors"
              >
                Source: IBM Cost of a Data Breach Report 2026
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="text-center" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold mb-4 text-white">See where you stand</h2>
              <p className="text-base text-neutral-400 mb-8">Free scan. No credit card.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/#scan"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors"
                >
                  Scan your app - free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/#pricing"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors border border-white/10"
                >
                  View pricing
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
